import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The panel that appears beside a shut category in the rail.
 *
 * Rendered through a portal, and that is the whole reason this file exists.
 *
 * It used to be an absolutely positioned child of the row, which worked only
 * because the rail deliberately had `overflow-visible` — and *that* was only
 * possible because the rail never scrolled. Making the rail stay put on a long
 * page means the rail needs its own scrollbar, a scrollbar means
 * `overflow-y: auto`, and CSS computes the other axis to `auto` as well the
 * moment one axis is not `visible`. So the panel — which sits outside the rail
 * by design — would have been clipped at the rail's right edge.
 *
 * `position: fixed` escapes any ancestor that clips. Same technique as
 * `Tooltip`, `HoverCard` and `ScrollX`.
 *
 * ## But into the React root, not `document.body`
 *
 * React 18 attaches its event listeners to the **root container**, so a portal
 * into `document.body` lands outside them and nothing inside it receives a
 * React event. The panel closed 180ms after the pointer arrived on it, because
 * its own `onMouseEnter` never fired — and the `Link` inside would have done a
 * full page reload instead of a client-side navigation, silently, for the same
 * reason.
 *
 * `Tooltip` and `HoverCard` portal to the body and are fine, which is why this
 * took a while to see: both are `pointer-events-none`, so neither has ever
 * needed an event on the portalled node. This is the first one that does.
 *
 * `#root` carries no transform, filter or containment, so `position: fixed`
 * inside it still resolves against the viewport — which is the only property of
 * `document.body` that mattered here.
 *
 * ## Every listener is bound to the row itself. There is no overlay.
 *
 * The first version put the hover handlers on a `<span className="absolute
 * inset-0">` covering the row, on the theory that keeping them off the button
 * stopped the two fighting over the pointer. What it actually did was cover the
 * button — an absolutely positioned sibling paints above a static one — so the
 * span swallowed every click. And because the panel only renders for a *shut*
 * category, and categories start shut, that meant **the entire menu was
 * unclickable**. Shipped, and reported within the hour.
 *
 * The listeners now go on the row element through its ref, which is where they
 * always belonged: nothing is layered over anything, and the button keeps its
 * own click.
 *
 * ## Why closing is delayed and opening is not
 *
 * There is a gap between the row and the panel. Moving the pointer across it
 * fires the row's `mouseleave` before the panel's `mouseenter`, so an immediate
 * close makes the panel vanish just as you reach for it — which is the same as
 * it not being clickable at all. A short grace period, cancelled by arriving at
 * either the row or the panel, is what makes the contents actually reachable.
 */

/**
 * Long enough to cross the gap between the row and the panel, short enough that
 * a panel left behind by a pointer moving on does not linger.
 */
const CLOSE_DELAY_MS = 180;

/**
 * The React root, which is where portals belong in this application.
 *
 * Falls back to `document.body` rather than throwing: a missing root would mean
 * the whole application had failed to mount, and a menu panel is not the place
 * to discover that.
 */
const portalHost = (): HTMLElement =>
  document.getElementById('root') ?? document.body;

export function NavFlyout({
  label,
  children,
  trigger,
}: {
  label: string;
  children: React.ReactNode;
  /** The row this belongs to. Measured for position, and listened to for hover. */
  trigger: React.RefObject<HTMLElement | null>;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const open = useCallback(() => {
    cancelClose();

    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;

    const height = panel.current?.getBoundingClientRect().height ?? 0;
    const margin = 8;

    /*
     * Beside the row, and nudged up if it would run off the bottom.
     *
     * The last category in the rail sits near the foot of the screen and its
     * panel is the tallest — System has eight items. Without the clamp it
     * renders half off screen, which is the first case somebody hits.
     */
    const top = Math.min(
      Math.max(rect.top, margin),
      Math.max(margin, window.innerHeight - height - margin),
    );

    /*
     * Measured from the **rail's** right edge, not the row's.
     *
     * The row sits inside the nav's 12px padding, so its right edge is short of
     * the rail's — and positioning from it put the panel 4px *over* the rail,
     * overlapping the thing it is meant to sit beside.
     */
    const rail = trigger.current?.closest('aside')?.getBoundingClientRect();
    const left = Math.max(rect.right, rail?.right ?? rect.right) + margin;

    setAt({ top, left });
  }, [trigger]);

  /** Delayed, so the pointer can cross the gap to the panel. */
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAt(null), CLOSE_DELAY_MS);
  }, []);

  const closeNow = useCallback(() => {
    cancelClose();
    setAt(null);
  }, []);

  /**
   * Hover and focus, both bound to the row.
   *
   * Focus matters as much as hover: the version this replaced used Tailwind's
   * `group-focus-within`, so tabbing onto a category revealed its contents, and
   * losing that would leave somebody driving the rail from the keyboard unable
   * to see what a collapsed category holds. Hover is an accelerator and must
   * never be the only way in.
   */
  useEffect(() => {
    const row = trigger.current;
    if (!row) return;

    const onBlur = (event: FocusEvent) => {
      // Not on the way between the row's own children.
      if (!row.contains(event.relatedTarget as Node | null)) closeSoon();
    };

    row.addEventListener('mouseenter', open);
    row.addEventListener('focus', open);
    row.addEventListener('blur', onBlur);
    return () => {
      row.removeEventListener('mouseenter', open);
      row.removeEventListener('focus', open);
      row.removeEventListener('blur', onBlur);
      cancelClose();
    };
  }, [trigger, open, closeSoon]);

  /** Re-measure once the panel is real, so the clamp has a height to work with. */
  useEffect(() => {
    if (at !== null) open();
    // Deliberately keyed on whether it is open, not on `at` — `at` is what this
    // writes, and depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at !== null]);

  /**
   * Closing is decided by where the pointer *is*, not by enter and leave events.
   *
   * The version this replaces listened for `mouseleave` on the row and
   * `mouseenter` on the panel, and it did not work. The events fired — measured,
   * both of them — and the panel still vanished before the pointer arrived,
   * because the ordering of leave against enter across a gap depends on things
   * that are not worth understanding: which element the browser considers the
   * pointer to have entered, whether a re-render swapped the node in between,
   * and how a synthesised move differs from a real one.
   *
   * One `mousemove` listener and two rectangles has none of that. The pointer is
   * either within the row, or within the panel, or within the gap between them —
   * and in all three cases the answer is obvious and the same every time.
   *
   * The padding is what makes the gap safe: it is the 8px margin plus a little,
   * so crossing from row to panel never reads as leaving both.
   */
  useEffect(() => {
    if (at === null) return;

    const PAD = 14;
    const within = (r: DOMRect | undefined, x: number, y: number) =>
      !!r && x >= r.left - PAD && x <= r.right + PAD && y >= r.top - PAD && y <= r.bottom + PAD;

    const onMove = (event: MouseEvent) => {
      const row = trigger.current?.getBoundingClientRect();
      const box = panel.current?.getBoundingClientRect();
      if (within(row, event.clientX, event.clientY) || within(box, event.clientX, event.clientY)) {
        cancelClose();
      } else {
        closeSoon();
      }
    };

    document.addEventListener('mousemove', onMove);
    /*
     * Scroll and resize still close it outright.
     *
     * A panel positioned against a row that has since moved points at nothing.
     * `capture` because the rail scrolls its own contents, and a scroll inside it
     * does not reach window otherwise.
     */
    window.addEventListener('scroll', closeNow, { passive: true, capture: true });
    window.addEventListener('resize', closeNow);
    return () => {
      document.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', closeNow, { capture: true });
      window.removeEventListener('resize', closeNow);
    };
  }, [at, trigger, closeSoon, closeNow]);

  if (at === null) return null;

  return createPortal(
    <div
      ref={panel}
      style={{ top: at.top, left: at.left }}
      className="fixed z-40 w-56"
      // Belt and braces. The mousemove test above is what actually keeps this
      // open; these cost nothing and cover a pointer that arrives without a move
      // event, which happens after a scroll or a tab switch.
      onMouseEnter={cancelClose}
    >
      {/*
        A tall group scrolls inside its own panel rather than being pushed up
        the screen until it runs out of room.

        The clamp above only ever *moved* the panel to keep it on screen, which
        works right up until a group has more items than the viewport has room
        for — after that the top is pinned at the margin and the rest is simply
        unreachable. A max-height with its own scroll means a long group can
        always be read to the end, on a laptop and on a short window alike.
      */}
      <div className="flex max-h-[calc(100vh-1rem)] flex-col overflow-y-auto overscroll-contain rounded-xl border border-navy-600 bg-navy-800 p-2">
        <p className="nav-group-label px-2 pb-1.5 pt-1">{label}</p>
        {/* Clicking a destination closes the panel: the page is changing, and a
            panel left open over the new one is a panel nobody asked for. */}
        <ul className="space-y-0.5" onClick={closeNow}>
          {children}
        </ul>
      </div>
    </div>,
    portalHost(),
  );
}
